import { QueryClient } from '@tanstack/vue-query';
import { type Locale, setDefaultOptions } from 'date-fns';
import { locales } from 'macro-i18n';
import { ref } from 'vue';
import {
  type ActiveToast,
  type ToastPluginApi,
  useToast,
} from 'vue-toast-notification';

import settings from '@scaffold/common/settings';

import type { RouteContext, RouteMiddleware } from '@/core/routing';
import { setCurrencyDefaults, setLocaleDefaults } from '@/core/utils';

export type AppContext = { queryClient: QueryClient };

export type PageContext = RouteContext<AppContext>;

export type PageMiddleware = RouteMiddleware<AppContext>;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

export const locale = ref<string>(locales[0]);

export function resolveLocalePath(href: string, locale: string) {
  const firstSegment = href.split('/')[1];
  if (locales.includes(firstSegment))
    href = href.slice(1 + firstSegment.length);

  return locale === locales[0] ? href : '/' + locale + href;
}

// Unused for now - kept for when locale switching comes back.
// export function switchLocale(locale: string) {
//   const oneYear = 60 * 60 * 24 * 365;
//   const secure = location.protocol === 'https:' ? ';Secure' : '';
//
//   document.cookie =
//     'firebase-language-override=;expires=Thu, 01 Jan 1970 00:00:01 GMT';
//   document.cookie =
//     'firebase-country-override=;expires=Thu, 01 Jan 1970 00:00:01 GMT';
//   document.cookie = `firebase-language-override=${locale};path=/;max-age=${oneYear};SameSite=Lax${secure}`;
//   document.cookie = `firebase-country-override=${locale};path=/;max-age=${oneYear};SameSite=Lax${secure}`;
//
//   location.reload();
// }

export async function setLocale(newLocale: string) {
  locale.value = newLocale;

  const localeModules: Partial<Record<string, () => Promise<Locale>>> = {
    'en-GB': () => import('date-fns/locale/en-GB').then(({ enGB }) => enGB),
    'de-DE': () => import('date-fns/locale/de').then(({ de }) => de),
  };

  setDefaultOptions({ locale: await localeModules[newLocale]?.() });

  setLocaleDefaults(newLocale);
  setCurrencyDefaults('en-GB', settings.payment.currency);
}

export const toast = import.meta.env.SSR
  ? new Proxy(
      {} as ToastPluginApi & {
        oops(): ActiveToast;
      },
      {
        get(_target, prop) {
          return (...args: unknown[]) => {
            console.log(`toast.${String(prop)}:`, ...args);
            return {
              dismiss() {
                console.log('ActiveToast.dismiss');
              },
            };
          };
        },
      },
    )
  : new Proxy(
      {} as ToastPluginApi & {
        oops(): ActiveToast;
      },
      {
        get(target, prop, receiver) {
          // Lazily initialize useToast
          if (!('__instance' in target)) {
            const __instance = {
              ...useToast(),
              oops() {
                return target.error($t`Oops! Something went wrong…`);
              },
            };

            Object.assign(target, {
              __instance,
              ...__instance,
            });
          }
          return Reflect.get(target, prop, receiver);
        },
      },
    );
